import { Form } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Gender,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import {
	GetFunctionaryResponse,
	PositionAssignmentRequest,
	RoleAssignment,
	PositionAssignmentFunctionaryInfo,
} from '../../../shared/MySrcm';
import { VIEW_FUNCTIONARIES } from '../../../shared/Routes';
import { useBatchId } from '../../../hooks/useBatchId';
import { useAuth } from '../../../hooks/useAuth';
import { useCallApi } from '../../../hooks/useCallApi';
import { getRoles } from '../../../service/RoleBasedService';
import {
	getAbhyasiDetails,
	getFunctionary,
	upsertPositionAssignment,
} from '../../../service/MySrcmService';
import { useTranslation } from 'react-i18next';
import { useToastMessage } from '../../../hooks/useToastMessage';
import { NavigateOptions, useLocation, useNavigate } from 'react-router-dom';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import { useConfirmPopup } from '../../../hooks/useConfirmPopup';
import dayjs from '../../../shared/Utils';
import {
	API_DATE_FORMAT,
	DATE_FORMAT_DD_MM_YYYY,
} from '../../../shared/Constants';

let globalIsEditFormStatus = false;
const listeners: (() => void)[] = [];

type FunctionaryMapping = {
	type: string;
	roleId?: number;
	authorizedZones: Array<{ value: string | number; label?: string }>;
	authorizedRegions: Array<{ value: string | number; label?: string }>;
	startDate?: string;
	endDate?: string;
	homeRegiom?: string;
	homeZone?: string;
	partnerId?: number;
};

type FunctionaryFormValues = PositionAssignmentFunctionaryInfo & {
	isActive?: boolean;
	functionaryMappings?: FunctionaryMapping[];
};

export type GroupData = {
	id: number;
	name: string;
	complete_name: string;
	group_resp: number;
	group_type: string;
	parent_id: number;
	zone_group: number;
	active: boolean;
};

export const useAddOrEditFunctionaryPage = () => {
	const [submitButtonDisabled, setSubmitButtonDisabled] =
		useState<boolean>(true);
	const { getRouteWithParams, queryParams } = useBatchId();
	const [form] = Form.useForm<FunctionaryFormValues>();
	const values = Form.useWatch<FunctionaryFormValues>([], form);
	const [functionary, setFunctionary] = useState<
		FunctionaryFormValues | undefined
	>();
	const [isEditFormStatus, setIsEditFormStatus] = useState<boolean>(
		globalIsEditFormStatus
	);
	const { srcmId } = useAuth();
	const { callApi } = useCallApi();
	const { t } = useTranslation();
	const { showToastMessage } = useToastMessage();
	const navigate = useNavigate();
	const { confirmPopUpAction } = useConfirmPopup();
	const { infoPopUpAction } = useInfoPopup();
	const location = useLocation();
	const roleIdCacheRef = useRef<Map<string, number>>(new Map());

	const fetchRoleId = useCallback(
		async (roleCode?: string) => {
			if (!roleCode) {
				return undefined;
			}
			const cached = roleIdCacheRef.current.get(roleCode);
			if (cached) {
				return cached;
			}
			if (!srcmId) {
				return undefined;
			}

			const response = await getRoles({
				loggedInUserSrcmId: srcmId,
				selectedRoleCode: roleCode,
				filters: { roleCode },
				pageIndex: 0,
				pageSize: 1,
			});
			const roleId = response.roles?.[0]?.id;
			if (roleId) {
				roleIdCacheRef.current.set(roleCode, roleId);
			}
			return roleId;
		},
		[srcmId]
	);

	const functionarySrcmIdParam = useMemo(() => {
		const functionarySIdParam = queryParams.get('functionarySrcmIdParam');
		if (typeof functionarySIdParam !== 'string') {
			return '-1';
		}

		return functionarySIdParam;
	}, [queryParams]);

	useEffect(() => {
		const editFunctionaryProposer =
			typeof location.state === 'object' &&
			location.state !== null &&
			'from' in location.state
				? (location.state as { from?: string }).from
				: undefined;
		if (editFunctionaryProposer === 'Edit Functionary/Proposer') {
			globalIsEditFormStatus = true;
			listeners.forEach((listener) => listener());
		}
	}, [location.state]);

	const navigateWithoutSrcmId = useCallback(
		(route: string, params?: NavigateOptions) => {
			navigate(`${getRouteWithParams(route, true)}`, params);
		},
		[getRouteWithParams, navigate]
	);

	const addOrEditFormStatus = useCallback(() => {
		globalIsEditFormStatus = true;
		listeners.forEach((listener) => listener());
	}, []);

	const addFunctionaryStatus = useCallback(() => {
		globalIsEditFormStatus = false;
		listeners.forEach((listener) => listener());
	}, []);

	if (!listeners.includes(() => setIsEditFormStatus(globalIsEditFormStatus))) {
		listeners.push(() => setIsEditFormStatus(globalIsEditFormStatus));
	}

	const normalizeRoleCode = (type?: string): string => {
		if (!type) return '';

		return type
			.replace(/^is_/i, '')
			.toLowerCase()
			.replace(/^\w/, (c) => c.toUpperCase());
	};

	const buildSaveFunctionaryOfZoneRequest = useCallback(
		async (functionaryInfo: FunctionaryFormValues) => {
			// 1. Generate the raw list of assignments (one for each mapping in the form)
			const rawAssignments = await Promise.all(
				(functionaryInfo.functionaryMappings || []).map(async (mapping) => {
					const roleCode = normalizeRoleCode(mapping.type)?.toUpperCase();
					const roleId = Number((await fetchRoleId(roleCode)) ?? 0);

					return {
						roleId,
						roleCode,
						authorizedRegions: (mapping.authorizedRegions || []).map((reg) => ({
							regionId: Number(reg.value),
							regionName: reg.label ?? '',
						})),
						authorizedZones: (mapping.authorizedZones || []).map((zone) => ({
							zoneId: Number(zone.value),
							zoneName: zone.label ?? '',
						})),
						startDate: mapping.startDate
							? dayjs(mapping.startDate, [
									API_DATE_FORMAT,
									DATE_FORMAT_DD_MM_YYYY,
							  ]).format(DATE_FORMAT_DD_MM_YYYY)
							: '',
						endDate: mapping.endDate
							? dayjs(mapping.endDate, [
									API_DATE_FORMAT,
									DATE_FORMAT_DD_MM_YYYY,
							  ]).format(DATE_FORMAT_DD_MM_YYYY)
							: '',
						homeRegion: mapping.homeRegiom || '',
						homeZone: mapping.homeZone || '',
						partnerId: mapping.partnerId,
					};
				})
			);

			// 2. Group the raw assignments by roleId
			const groupedAssignments = rawAssignments.reduce((acc, curr) => {
				const existing = acc.find((item) => item.roleId === curr.roleId);

				if (existing) {
					// Merge Regions without duplicates
					curr.authorizedRegions.forEach((reg) => {
						if (
							!existing.authorizedRegions.some(
								(r) => r.regionId === reg.regionId
							)
						) {
							existing.authorizedRegions.push(reg);
						}
					});

					// Merge Zones without duplicates
					curr.authorizedZones.forEach((zone) => {
						if (
							!existing.authorizedZones.some((z) => z.zoneId === zone.zoneId)
						) {
							existing.authorizedZones.push(zone);
						}
					});
				} else {
					// First time seeing this roleId, add it to the list
					acc.push(curr);
				}
				return acc;
			}, [] as RoleAssignment[]);

			// 3. Return the final object
			return {
				name: functionaryInfo?.printName ?? '',
				email: functionaryInfo?.email ?? '',
				mobile: functionaryInfo?.mobile ?? '',
				srcmId: functionaryInfo?.srcmId ?? '',
				homeZone: functionaryInfo?.homeZone ?? '',
				homeRegion: functionaryInfo?.homeRegion ?? '',
				partnerId: functionaryInfo?.partnerId ?? 0,
				gender: (functionaryInfo?.gender ?? 'NOT_PROVIDED') as Gender,
				isActive: isEditFormStatus ? functionaryInfo.isActive ?? true : true,
				roleAssignments: groupedAssignments, // Now this is in scope and correctly merged
			};
		},
		[fetchRoleId, isEditFormStatus]
	);

	const onConfirmSubmit = useCallback(async () => {
		if (functionary) {
			const saveFunctionaryOfZoneRequest =
				await buildSaveFunctionaryOfZoneRequest(functionary);
			const bulkRequest: PositionAssignmentRequest = {
				functionaries: [saveFunctionaryOfZoneRequest],
				functionarySrcmId: srcmId ?? '',
			};
			try {
				const response = await callApi(
					() => upsertPositionAssignment(bulkRequest),
					'errorOccurredWhileSubmittingApplication'
				);
				if (response) {
					const successContent = t('processingDetails');
					infoPopUpAction(
						t('success'),
						successContent,
						() => {
							navigateWithoutSrcmId(VIEW_FUNCTIONARIES);
						},
						ConfirmTick
					);
				} else {
					infoPopUpAction(
						'Error',
						t('errorOccurredWhileSubmittingApplication'),
						() => {},
						null
					);
				}
			} catch (e) {
				// handled in callApi
			}
		}
	}, [
		functionary,
		buildSaveFunctionaryOfZoneRequest,
		srcmId,
		callApi,
		infoPopUpAction,
		t,
		navigateWithoutSrcmId,
	]);

	const onSubmitClick = useCallback(() => {
		confirmPopUpAction(
			'confirmSubmit',
			'areYouSureYouWantToSubmit',
			onConfirmSubmit
		);

		if (isEditFormStatus) {
			addOrEditFormStatus();
		}
	}, [
		onConfirmSubmit,
		confirmPopUpAction,
		isEditFormStatus,
		addOrEditFormStatus,
	]);

	const onCancelClick = useCallback(() => {
		navigateWithoutSrcmId(VIEW_FUNCTIONARIES);
		if (isEditFormStatus) {
			addOrEditFormStatus();
		}
	}, [navigateWithoutSrcmId, isEditFormStatus, addOrEditFormStatus]);

	const handleFormValuesChange = useCallback(async () => {
		try {
			const fieldValues = await form.validateFields({ validateOnly: true });

			const mappings = fieldValues.functionaryMappings || [];

			const isFunctionaryValid =
				mappings.length > 0 ||
				mappings.every((mapping: FunctionaryMapping) => {
					const shouldHideAuthorizedFields = [
						UserRole.CARE_TEAM,
						UserRole.PREWORK_MENTOR,
						UserRole.SNP_ADMIN,
					].includes(mapping?.type as UserRole);

					if (shouldHideAuthorizedFields) {
						return mapping?.type && mapping?.startDate;
					}
					return (
						mapping?.type &&
						mapping?.authorizedRegions?.length > 0 &&
						mapping?.authorizedZones?.length > 0
					);
				});

			setFunctionary(fieldValues);
			setSubmitButtonDisabled(!isFunctionaryValid);
		} catch (e) {
			const fields = e as { values: FunctionaryFormValues; outOfDate: boolean };

			if (!fields.outOfDate) {
				const mappings = fields.values?.functionaryMappings || [];

				const isFunctionaryValid =
					mappings.length === 0 ||
					mappings.every((mapping: FunctionaryMapping) => {
						return (
							mapping?.type &&
							mapping?.authorizedRegions?.length > 0 &&
							mapping?.authorizedZones?.length > 0
						);
					});

				setFunctionary(fields.values);
				setSubmitButtonDisabled(!isFunctionaryValid);
			}
		}
	}, [form]);

	useEffect(() => {
		void handleFormValuesChange();
	}, [handleFormValuesChange, values]);

	const buildFunctionaryInfo = useCallback(
		(response: GetFunctionaryResponse): FunctionaryFormValues => {
			const functionaryProfile = response?.functionary;
			const roleAssignments: RoleAssignment[] =
				response?.functionary?.roleAssignments ?? [];

			if (!functionaryProfile) {
				return {
					printName: '',
					email: '',
					mobile: '',
					srcmId: '',
					gender: undefined,
					functionaryType: undefined,
					zones: [],
					regions: [],
					functionaryMappings: [],
					isActive: false,
				};
			}

			// 1. Group the flat list from the API by roleCode (or roleId)
			const groupedMappings = roleAssignments.reduce((acc, ra) => {
				// Find if we already processed this role
				const existing = acc.find((item) => item.type === ra.roleCode);

				const regions = (ra.authorizedRegions ?? []).map((r) => ({
					value: r.regionId,
					label: r.regionName,
				}));

				const zones = (ra.authorizedZones ?? []).map((z) => ({
					value: z.zoneId,
					label: z.zoneName,
				}));

				if (existing) {
					// Add regions if they don't already exist in the 'existing' object
					regions.forEach((reg) => {
						if (
							!existing.authorizedRegions.some((r) => r.value === reg.value)
						) {
							existing.authorizedRegions.push(reg);
						}
					});

					// Add zones if they don't already exist
					zones.forEach((zone) => {
						if (!existing.authorizedZones.some((z) => z.value === zone.value)) {
							existing.authorizedZones.push(zone);
						}
					});
				} else {
					// New role found, create the entry
					acc.push({
						roleId: Number(ra.roleId),
						type: ra.roleCode,
						authorizedRegions: regions,
						authorizedZones: zones,
						startDate: ra.startDate,
						endDate: ra.endDate,
					});
				}
				return acc;
			}, [] as FunctionaryMapping[]);

			// 2. Map legacy top-level fields (if still needed by your form)
			const allZones = roleAssignments.flatMap((ra) =>
				(ra.authorizedZones ?? []).map((z) => ({ value: String(z.zoneId) }))
			);

			const allRegions = roleAssignments.flatMap((ra) =>
				(ra.authorizedRegions ?? []).map((r) => ({
					value: r.regionId,
					label: r.regionName,
				}))
			);

			return {
				printName: functionaryProfile.name ?? '',
				email: functionaryProfile.email ?? '',
				mobile: functionaryProfile.mobile ?? '',
				srcmId: functionaryProfile.srcmId,
				gender: functionaryProfile.gender,
				functionaryType: roleAssignments[0]?.roleCode,
				zones: allZones,
				regions: allRegions,
				functionaryMappings: groupedMappings, // This now contains the clubbed roles
				isActive: true,
			};
		},
		[]
	);

	const fetchPreceptorByAbhyasiIdFunctionary = useCallback(
		async (abhyasiId: string) => {
			try {
				const response = await callApi(
					() => getAbhyasiDetails(abhyasiId),
					'errorOccurredWhileFetchingPreceptorDetails'
				);
				return response.results?.[0];
			} catch (e) {
				/* empty */
			}
		},
		[callApi]
	);

	const loadProposer = useCallback(async () => {
		if (functionarySrcmIdParam && functionarySrcmIdParam !== '-1') {
			try {
				// 1. Get functionary details
				const response = await getFunctionary({
					functionarySrcmId: functionarySrcmIdParam,
				});

				const functionaryInfo = buildFunctionaryInfo(response);

				setFunctionary(functionaryInfo);

				form.setFieldsValue({
					...functionaryInfo,
					functionaryMappings: functionaryInfo.functionaryMappings ?? [],
				});

				setSubmitButtonDisabled(false);
			} catch (e) {
				if (e instanceof Error) {
					showToastMessage('error', e.message);
				} else {
					showToastMessage('error', 'Unknown error occurred');
				}
			}
		}
	}, [buildFunctionaryInfo, form, functionarySrcmIdParam, showToastMessage]);

	useEffect(() => {
		void loadProposer();
	}, [loadProposer]);

	return {
		form,
		functionary,
		submitButtonDisabled,
		onSubmitClick,
		onCancelClick,
		values,
		isEditFormStatus,
		addOrEditFormStatus,
		addFunctionaryStatus,
		functionarySrcmIdParam,
		fetchPreceptorByAbhyasiIdFunctionary,
		setSubmitButtonDisabled,
	};
};
